import { isBluetoothTransportAvailable } from "@/lib/runtime";
import {
  BluetoothLineBuffer,
  encodeBluetoothLine,
} from "@/lib/bluetoothLineBuffer";

export type BluetoothLineHandler = (line: string) => void;

export interface BluetoothOptions {
  /** BLE service UUID providing the data characteristic */
  serviceUuid?: string;
  /** BLE characteristic UUID that notifies line-delimited data */
  characteristicUuid?: string;
  /** Optional write characteristic UUID for sending data to the device */
  writeCharacteristicUuid?: string;
  /** Optional name prefix to filter devices in the chooser */
  deviceNamePrefix?: string;
  /** Optional device ID for reconnecting without prompting */
  deviceId?: string;
  /** When reconnecting to a remembered device, open the chooser if it is unavailable */
  allowDeviceChooserFallback?: boolean;
}

export const DEFAULT_BLE_SERVICE_UUID =
  "6e400001-b5a3-f393-e0a9-e50e24dcca9e";
export const DEFAULT_BLE_NOTIFY_UUID =
  "6e400003-b5a3-f393-e0a9-e50e24dcca9e";
export const DEFAULT_BLE_WRITE_UUID =
  "6e400002-b5a3-f393-e0a9-e50e24dcca9e";

type BluetoothWithKnownDevices = Bluetooth & {
  getDevices?: () => Promise<BluetoothDevice[]>;
};

type NavigatorWithBluetoothDevices = Navigator & {
  bluetooth: BluetoothWithKnownDevices;
};

export class WebBluetoothClient {
  private device: BluetoothDevice | null = null;
  private server: BluetoothRemoteGATTServer | null = null;
  private characteristic: BluetoothRemoteGATTCharacteristic | null = null;
  private writeCharacteristic: BluetoothRemoteGATTCharacteristic | null = null;
  private onDisconnect?: () => void;
  private lineBuffer: BluetoothLineBuffer;
  private decoder = new TextDecoder();
  private encoder = new TextEncoder();
  private suppressDisconnectEvent = false;

  constructor(onLine: BluetoothLineHandler, onDisconnect?: () => void) {
    this.onDisconnect = onDisconnect;
    this.lineBuffer = new BluetoothLineBuffer(onLine, "WebBluetooth");
  }

  /** Check if browser supports Web Bluetooth */
  get isSupported(): boolean {
    return isBluetoothTransportAvailable();
  }

  get connectedDeviceId(): string | null {
    return this.device?.id ?? null;
  }

  get connectedDeviceName(): string | null {
    return this.device?.name ?? null;
  }

  async listDevices(): Promise<BluetoothDevice[]> {
    if (!this.isSupported) return [];
    const bluetooth = (navigator as NavigatorWithBluetoothDevices).bluetooth;
    if (!bluetooth || typeof bluetooth.getDevices !== "function") return [];
    try {
      return await bluetooth.getDevices();
    } catch {
      return [];
    }
  }

  private async getKnownDevice(deviceId: string): Promise<BluetoothDevice | null> {
    const devices = await this.listDevices();
    return devices.find((device) => device.id === deviceId) ?? null;
  }

  /** Connect + start notifications (idempotent) */
  async connect(options: BluetoothOptions = {}): Promise<void> {
    if (!this.isSupported) {
      throw new Error("Web Bluetooth API not supported in this browser.");
    }

    if (this.device?.gatt?.connected && this.characteristic) {
      return;
    }

    const serviceUuid = options.serviceUuid ?? DEFAULT_BLE_SERVICE_UUID;
    const characteristicUuid =
      options.characteristicUuid ?? DEFAULT_BLE_NOTIFY_UUID;
    const writeUuid = options.writeCharacteristicUuid ?? DEFAULT_BLE_WRITE_UUID;
    const allowDeviceChooserFallback = options.allowDeviceChooserFallback === true;

    let device: BluetoothDevice | null = null;
    if (options.deviceId) {
      device = await this.getKnownDevice(options.deviceId);
      if (!device && !allowDeviceChooserFallback) {
        throw new Error("Known Bluetooth device not available.");
      }
    }

    if (!device) {
      const filters: BluetoothLEScanFilter[] = options.deviceNamePrefix
        ? [{ namePrefix: options.deviceNamePrefix }]
        : [{ services: [serviceUuid] }];

      device = await navigator.bluetooth.requestDevice({
        filters,
        optionalServices: [serviceUuid],
      });
    }

    device.addEventListener("gattserverdisconnected", this.handleDisconnect);

    const server = await device.gatt?.connect();
    if (!server) {
      throw new Error("Failed to connect to Bluetooth device.");
    }

    const service = await server.getPrimaryService(serviceUuid);
    const characteristic = await service.getCharacteristic(characteristicUuid);

    this.lineBuffer.reset();
    characteristic.addEventListener(
      "characteristicvaluechanged",
      this.handleValueChanged
    );
    try {
      await characteristic.startNotifications();
    } catch (err) {
      characteristic.removeEventListener(
        "characteristicvaluechanged",
        this.handleValueChanged
      );
      throw err;
    }

    let writeCharacteristic: BluetoothRemoteGATTCharacteristic | null = null;
    try {
      writeCharacteristic = await service.getCharacteristic(writeUuid);
    } catch {
      writeCharacteristic = null;
    }

    this.device = device;
    this.server = server;
    this.characteristic = characteristic;
    this.writeCharacteristic = writeCharacteristic;
  }

  /** Disconnect cleanly & idempotently */
  async disconnect(): Promise<void> {
    if (!this.device && !this.characteristic) return;

    try {
      this.suppressDisconnectEvent = true;
      if (this.characteristic) {
        try {
          this.characteristic.removeEventListener(
            "characteristicvaluechanged",
            this.handleValueChanged
          );
          await this.characteristic.stopNotifications();
        } catch (err) {
          console.warn("[WebBluetooth] stopNotifications error:", err);
        }
      }

      if (this.device) {
        try {
          this.device.removeEventListener(
            "gattserverdisconnected",
            this.handleDisconnect
          );
          this.device.gatt?.disconnect();
        } catch (err) {
          console.warn("[WebBluetooth] disconnect error:", err);
        }
      }
    } finally {
      this.device = null;
      this.server = null;
      this.characteristic = null;
      this.writeCharacteristic = null;
      this.lineBuffer.reset();
      this.suppressDisconnectEvent = false;
    }
  }

  /** Optional write helper for line-based protocols */
  async writeLine(line: string): Promise<void> {
    if (!this.writeCharacteristic) {
      throw new Error("Bluetooth write characteristic not available.");
    }
    const payload = encodeBluetoothLine(this.encoder, line);
    await this.writeCharacteristic.writeValue(payload);
  }

  private handleValueChanged = (event: Event): void => {
    const target = event.target as BluetoothRemoteGATTCharacteristic | null;
    const value = target?.value;
    if (!value) return;
    const chunk = this.decoder.decode(value);
    this.lineBuffer.pushChunk(chunk);
  };

  private handleDisconnect = (): void => {
    console.warn("[WebBluetooth] Device disconnected");
    this.device = null;
    this.server = null;
    this.characteristic = null;
    this.writeCharacteristic = null;
    this.lineBuffer.reset();
    if (!this.suppressDisconnectEvent) {
      this.onDisconnect?.();
    }
  };
}
